import { resolveSidebarLabel } from "../sidebarLabels";
import { filterVisibleOptions } from "./sidebarVisibility";

export const getSectionKey = (tabName) => `section:${tabName}`;
export const getOptionKey = (tabName, optionId) =>
  `option:${tabName}:${optionId}`;

export const getOptionDisplayLabel = (option) =>
  option.labelKey != null ? resolveSidebarLabel(option.labelKey) : option.label;

export const optionMatchesSelection = (option, selected) => {
  if (selected == null) return false;
  if (option.labelKey != null) {
    return (
      selected === option.labelKey ||
      selected === resolveSidebarLabel(option.labelKey)
    );
  }
  return selected === option.label;
};

const SectionIcon = ({ icon, iconClassName = "" }) => (
  <span className="sider-menu-icon-wrapper">
    <img
      className={["sider-menu-icon", iconClassName]
        .filter(Boolean)
        .join(" ")}
      src={icon}
      alt=""
    />
  </span>
);

const SectionLabel = ({
  tabName,
  labelKey,
  dropdownMenuClassName = "",
}) => (
  <span
    className="sider-layer-one-label"
    data-testid={`sider-tab-${tabName}`}
    data-menu-class={dropdownMenuClassName}
  >
    {labelKey != null ? resolveSidebarLabel(labelKey) : tabName}
  </span>
);

const buildOptionItems = ({
  tabName,
  options,
  parentKey,
  optionByKey,
  parentKeyByKey,
}) =>
  options.map((option) => {
    const key = getOptionKey(tabName, option.id);
    const hasChildren =
      Array.isArray(option.children) && option.children.length > 0;

    optionByKey.set(key, { option, tabName });
    parentKeyByKey.set(key, parentKey);

    return {
      key,
      className: option.labelClassName,
      style: option.labelStyle,
      disabled: !option.navigate && !hasChildren,
      label: (
        <span data-testid={`dropdown-${tabName}-${option.id}`}>
          {getOptionDisplayLabel(option)}
        </span>
      ),
      children: hasChildren
        ? buildOptionItems({
            tabName,
            options: option.children,
            parentKey: key,
            optionByKey,
            parentKeyByKey,
          })
        : undefined,
    };
  });

export const buildSiderMenuModel = ({
  sections,
  isExpanded,
}) => {
  const sectionByKey = new Map();
  const optionByKey = new Map();
  const parentKeyByKey = new Map();

  const items = sections.map((section) => {
    const key = getSectionKey(section.tabName);
    const visibleOptions = filterVisibleOptions(section.options ?? []);
    const hasChildren = visibleOptions.length > 0;
    const sectionTitle =
      section.labelKey != null
        ? resolveSidebarLabel(section.labelKey)
        : section.tabName;

    sectionByKey.set(key, { ...section, hasChildren });

    return {
      key,
      icon: (
        <SectionIcon
          icon={section.icon}
          iconClassName={section.iconClassName}
        />
      ),
      label: <SectionLabel {...section} />,
      title: isExpanded ? false : sectionTitle,
      children: hasChildren && isExpanded
        ? buildOptionItems({
            tabName: section.tabName,
            options: visibleOptions,
            parentKey: key,
            optionByKey,
            parentKeyByKey,
          })
        : undefined,
    };
  });

  return {
    items,
    sectionByKey,
    optionByKey,
    parentKeyByKey,
    rootKeys: new Set(sectionByKey.keys()),
  };
};

export const resolveSelectedOptionKey = ({
  optionByKey,
  selectedSiderTab,
  selectedDropdownOption,
  pathname,
}) => {
  const activeOptions = [...optionByKey.entries()].filter(
    ([, entry]) => entry.tabName === selectedSiderTab,
  );
  const selectedEntry =
    activeOptions.find(([, entry]) => entry.option.navigate === pathname) ??
    activeOptions.find(([, entry]) =>
      entry.option.labelKey != null
        ? selectedDropdownOption === entry.option.labelKey
        : selectedDropdownOption === entry.option.label,
    ) ??
    activeOptions.find(([, entry]) =>
      optionMatchesSelection(entry.option, selectedDropdownOption),
    );

  return selectedEntry?.[0] ?? null;
};

export const resolveAncestorKeys = ({
  selectedOptionKey,
  parentKeyByKey,
  selectedRootKey,
}) => {
  const ancestorKeys = [];
  let parentKey = selectedOptionKey
    ? parentKeyByKey.get(selectedOptionKey)
    : null;

  while (parentKey && parentKey !== selectedRootKey) {
    ancestorKeys.unshift(parentKey);
    parentKey = parentKeyByKey.get(parentKey);
  }

  return ancestorKeys;
};
